import { colors } from "constants/colors";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Document } from "components/icons/Document";
import { LinkBreak } from "components/icons/LinkBreak";
import { Padlock } from "components/icons/Padlock";
import { SmallArrowUpRight } from "components/icons/SmallArrowUpRight";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useHistory, useLocation } from "react-router-dom";
import { useUnlinkInvoicesMutation } from "store/apis/transactions";
import { Invoice } from "types/Models/invoice";
import { BackendError } from "types/utils/error";

const BillItem = ({
  uuid,
  billNumber,
  transactionId,
  financialRecordType,
}: {
  uuid: string;
  billNumber: string;
  transactionId: string;
  financialRecordType: string;
}) => {
  const { search } = useLocation();
  const history = useHistory();
  const confirmUnlinkModal = useModal();
  const hasExpenseAccess = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE,
    "READ_ONLY"
  );
  const [unlinkInvoices, { isLoading: unlinkingBill }] =
    useUnlinkInvoicesMutation();
  const entityId = useCurrentEntityId();
  const group = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();

  const handleUnlinkBill = async () => {
    try {
      await unlinkInvoices({
        entityId,
        transactionId,
        financial_record_type: financialRecordType,
        groupId: group.uuid,
        invoice_id: uuid,
      }).unwrap();
      successToast({ message: "Bill unlinked successfully" });
      confirmUnlinkModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  return (
    <div className="t-flex t-justify-between t-gap-2 t-h-full t-items-center">
      <div className="t-w-full t-p-1 t-border t-border-solid t-border-neutral-10 t-flex t-gap-2 t-rounded t-items-center">
        <Document color={colors.neutral[70]} />
        <div className="t-text-body-sm t-text-text-100">{billNumber}</div>
      </div>

      <div className="t-flex t-h-full t-items-center t-gap-2">
        <Modal.Root
          open={confirmUnlinkModal.isOpen}
          onOpenChange={confirmUnlinkModal.toggle}
        >
          <Modal.Trigger asChild>
            <Button customType="icon" size="xsmall">
              <LinkBreak />
            </Button>
          </Modal.Trigger>
          <Modal.Content size="small">
            <Modal.Body>
              <div>
                <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
                  <TriangleWarning />
                </span>
                <p className="t-mb-2 t-text-body-sm t-font-medium t-mt-5">
                  Unlink bill?
                </p>
                <div className="t-text-body-sm t-text-text-30">
                  Unlinking the bill will change the bill status and will
                  uncategorize the corresponding transaction.
                </div>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button>Cancel</Button>
              </Modal.RawClose>
              <Button
                isLoading={unlinkingBill}
                disabled={unlinkingBill}
                customType="primary"
                onClick={handleUnlinkBill}
              >
                Confirm
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        </Modal.Root>

        <Button
          customType="icon"
          size="xsmall"
          onClick={() =>
            history.push(`/books/bills-and-vendors/${uuid}${search}`)
          }
          disabled={!hasExpenseAccess}
        >
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.EXPENSE}
            level="READ_ONLY"
            blockedUi={<Padlock />}
          >
            <SmallArrowUpRight />
          </LockedUi>
        </Button>
      </div>
    </div>
  );
};

export const LinkedBills = ({
  transactionId,
  invoices,
}: {
  transactionId: string;
  invoices: Invoice[];
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-2">
      {invoices.map(({ uuid, invoice_number, financial_record_type }) => (
        <BillItem
          key={uuid}
          billNumber={invoice_number}
          uuid={uuid}
          transactionId={transactionId}
          financialRecordType={financial_record_type}
        />
      ))}
    </div>
  );
};
